import { useEffect, useRef, useState } from 'react'
import { fetchRbacValidAssignmentQualifiers } from '../../services/rbacApi'

// Las características (qualifiers) se filtran entre sí: al seleccionar una,
// las demás muestran las opciones relacionadas. Este hook comprueba.

export default function useQualifierReconciliation(resource, value, onChange) {
  // Compara el contenido de las selecciones sin depender de la identidad del objeto.
  // La referencia latest mantiene actualizados el valor y la función onChange.
  const snapshot = JSON.stringify(value.qualifiers || {})
  const previous = useRef(value.qualifiers || {})
  // Conserva las claves pendientes si una petición se interrumpe o falla.
  const pending = useRef(new Set())
  const latest = useRef({ value, onChange, snapshot })
  latest.current = { value, onChange, snapshot }
  const [checking, setChecking] = useState([])
  const [error, setError] = useState('')
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    const qualifiers = JSON.parse(snapshot)
    // Solo las eliminaciones disparan la reconciliación. Revisa las otras características
    // con selecciones, conservando lo que el usuario dejó en las que acaba de editar.
    const removedKeys = Object.keys(previous.current).filter((key) =>
      previous.current[key].some((item) => !(qualifiers[key] || []).includes(item))
    )
    if (removedKeys.length) {
      Object.keys(qualifiers).forEach((key) => {
        if (!removedKeys.includes(key) && qualifiers[key].length) pending.current.add(key)
      })
    }
    previous.current = qualifiers
    const keys = [...pending.current].filter((key) => qualifiers[key]?.length)
    if (!keys.length) {
      pending.current.clear()
      setChecking([])
      setError('')
      return undefined
    }

    let cancelled = false
    // Ignora respuestas si el efecto se limpió o la selección cambió,
    // incluso si el siguiente efecto todavía no se ha ejecutado.
    const isCancelled = () => cancelled || latest.current.snapshot !== snapshot
    setChecking(keys)
    setError('')
    fetchRbacValidAssignmentQualifiers(resource, qualifiers)
      .then(({ qualifiers: valid }) => {
        if (isCancelled()) return
        pending.current.clear()
        setChecking([])
        const entries = keys.map((key) => [key, valid[key] || []])
        if (!entries.some(([key, items]) => items.length !== qualifiers[key].length)) return
        const nextQualifiers = { ...qualifiers, ...Object.fromEntries(entries) }
        // Registra la selección reconciliada antes de notificar al padre para que estas
        // eliminaciones automáticas no disparen otro ciclo de reconciliación.
        previous.current = nextQualifiers
        latest.current.onChange({ ...latest.current.value, qualifiers: nextQualifiers })
      })
      .catch(() => {
        if (isCancelled()) return
        // Conserva las selecciones y las claves pendientes para reintentar la misma validación.
        cancelled = true
        setChecking([])
        setError(
          'No se pudieron comprobar las selecciones relacionadas. Conservamos lo que marcaste; vuelve a intentarlo.'
        )
      })
    return () => {
      cancelled = true
    }
  }, [resource, snapshot, attempt])

  return { checking, error, retry: () => setAttempt((current) => current + 1) }
}
